import type { ReactNode } from "react";
import type {
  InventionElementResponse,
  InventionGateResponse,
} from "@patent-studio/api-types";
import { plural } from "@/lib/plural";
import { StageFooter, type FooterCondition } from "../layout/StageFooter";
import { CHECKING_FOR_QUESTIONS } from "../questions/useQuestionGate";
import { elementCardId } from "./anchors";

const CLOSED_HEADING = "Confirm the elements to continue";
const CLOSED_DETAIL =
  "Confirm, amend or remove every element in order to continue.";
const READY_HEADING = "Ready to confirm the elements";
const SETTLED_HEADING = "Invention confirmed";
export const CONTINUE = "Continue to Novelty";

/** "all 6 elements together, with 2 unverified elements carried forward" */
function together(elements: readonly InventionElementResponse[]): string {
  const unverified = elements.filter(
    (e) => e.source_tag === "unverified",
  ).length;
  const all = `all ${plural(elements.length, "element")} together`;
  if (unverified === 0) return all;
  return `${all}, with ${plural(unverified, "unverified element")} carried forward`;
}

/** Where a condition's first item lives on the page. */
function targetOf(condition: FooterCondition): string | null {
  return condition.first_id === null ? null : elementCardId(condition.first_id);
}

/**
 * The gate out of Invention, as the server projects it. Shut while any
 * element awaits attention, naming the count and landing on the first; open
 * once none does; and, once the attorney has confirmed the page, the way on to
 * Novelty.
 * Nothing is frozen by confirming: the elements stay editable, and the next
 * write that moves them shuts the gate again.
 */
export function InventionFooter({
  gate,
  elements,
  settling,
  asking = false,
  refusal,
  onSettle,
  onContinue,
  above,
}: {
  gate: InventionGateResponse;
  elements: readonly InventionElementResponse[];
  /** A confirmation is in flight. */
  settling: boolean;
  /** Continue is asking whether Praxis has questions first. */
  asking?: boolean;
  /** What the server said when it refused the last confirmation, or nothing. */
  refusal: string | null;
  onSettle: () => void;
  onContinue: () => void;
  /** Kept in view above the bar: the banner over the page's suggested changes. */
  above?: ReactNode;
}) {
  const settled = gate.settled;
  const heading = settled
    ? SETTLED_HEADING
    : gate.ready
      ? READY_HEADING
      : CLOSED_HEADING;
  const detail = settled
    ? `You confirmed ${together(elements)}. Novelty is now open.`
    : gate.ready
      ? `Confirming records ${together(elements)}.`
      : CLOSED_DETAIL;
  return (
    <StageFooter
      testId="invention-footer"
      heading={heading}
      detail={detail}
      ready={gate.ready}
      settled={settled}
      submitting={settling || asking}
      label={settled ? CONTINUE : "Confirm elements"}
      pendingLabel={asking ? CHECKING_FOR_QUESTIONS : "Confirming…"}
      onSubmit={settled ? onContinue : onSettle}
      sticky
      wash
      above={above}
      conditions={gate.conditions}
      resolveTarget={targetOf}
    >
      {refusal !== null && (
        <p role="alert" className="mt-2 text-[11.5px] text-destructive">
          {refusal}
        </p>
      )}
    </StageFooter>
  );
}
